import { useState } from 'react';
import { Button, Card, Icon, IconButton, Switch, Tag } from '../ds';
import { Avatar, Eyebrow, Mono, Screen } from '../components/primitives';
import { Field, TextInput } from './PrepScreen';
import { useToast } from '../components/Toast';
import * as api from '../lib/db';
import { useAuth } from '../state/AuthContext';
import { useFamily } from '../state/FamilyContext';
import { useTheme } from '../state/ThemeContext';
import type { ThemePreference } from '../state/ThemeContext';
import { roleTone } from '../lib/labels';
import { ageLabel } from '../lib/time';
import type { ParentRole } from '../lib/types';

/**
 * Settings.
 *
 * Not in the original design — the prototype exposed theme, default screen and
 * the milestones toggle as design-tool props, and hardcoded the names. A real
 * app needs somewhere to put the invite code, the grandparents' share link, the
 * handover time, night mode, and the family's own names.
 */

export function SettingsScreen({ onClose }: { onClose: () => void }) {
  const { family, familyId, codes, members, me, children } = useFamily();
  const { user, signOut } = useAuth();
  const { preference, setPreference } = useTheme();
  const toast = useToast();

  const [handover, setHandover] = useState(family?.handoverAt ?? '13:00');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(me?.name ?? '');

  const parents = members.filter((m) => m.role !== 'viewer');
  const viewers = members.filter((m) => m.role === 'viewer');
  const isParent = me && me.role !== 'viewer';

  const shareUrl =
    codes?.shareCode && typeof window !== 'undefined'
      ? `${window.location.origin}/?share=${codes.shareCode}`
      : '';

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.show({ title: `${label}をコピーしました` });
    } catch {
      toast.show({ title: 'コピーできませんでした', message: text, tone: 'danger' });
    }
  };

  const share = async () => {
    if (!shareUrl) return;
    // Native share sheet where it exists — that is how a link actually gets to
    // a grandparent — with a clipboard fallback everywhere else.
    if (navigator.share) {
      try {
        await navigator.share({ title: 'アルバム', text: 'アルバムを共有します', url: shareUrl });
        return;
      } catch {
        // Cancelled or unsupported — fall through to copying.
      }
    }
    await copy(shareUrl, '共有リンク');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 'calc(14px + env(safe-area-inset-top, 0px)) 16px 12px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <IconButton icon="ChevronLeft" label="もどる" variant="ghost" onClick={onClose} />
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'var(--text-lg)',
            margin: 0,
            flex: 1,
          }}
        >
          設定
        </h1>
      </div>

      <div className="scroll-area" style={{ flex: 1, minHeight: 0, maxWidth: 520, margin: '0 auto', width: '100%' }}>
        <Screen>
          {/* ---- me ---- */}
          <Eyebrow style={{ marginBottom: 8 }}>あなた · You</Eyebrow>
          <Card padding="md" style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {me ? (
                <Avatar
                  initial={me.initial}
                  color={me.role === 'viewer' ? 'var(--sand-500)' : roleTone(me.role as ParentRole).solid}
                  size={40}
                />
              ) : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{me?.name}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                  {user?.email}
                </div>
              </div>
              <IconButton
                icon="Pencil"
                label="名前を変える"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNameDraft(me?.name ?? '');
                  setEditingName((v) => !v);
                }}
              />
            </div>

            {editingName ? (
              <div style={{ marginTop: 14 }}>
                <Field label="呼びかた · Display name">
                  <TextInput value={nameDraft} onChange={setNameDraft} autoFocus />
                </Field>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!nameDraft.trim()}
                  onClick={async () => {
                    if (!familyId || !me) return;
                    await api.updateMemberProfile(familyId, me.uid, {
                      name: nameDraft.trim(),
                      initial: nameDraft.trim().slice(0, 1),
                    });
                    setEditingName(false);
                    toast.show({ title: '名前を変えました' });
                  }}
                >
                  保存
                </Button>
              </div>
            ) : null}
          </Card>

          {/* ---- night mode ---- */}
          <Eyebrow style={{ marginBottom: 8 }}>画面 · Appearance</Eyebrow>
          <Card padding="md" style={{ marginBottom: 18 }}>
            <div className="cjk" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 10 }}>
              夜中の授乳でも目にやさしい暗い画面です。
            </div>
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {(
                [
                  ['light', 'あかるい', 'Sun'],
                  ['dark', 'よる', 'Moon'],
                  ['system', '端末に合わせる', 'MonitorSmartphone'],
                ] as [ThemePreference, string, string][]
              ).map(([value, label, icon]) => (
                <Tag key={value} icon={icon} selected={preference === value} onClick={() => setPreference(value)}>
                  {label}
                </Tag>
              ))}
            </div>
          </Card>

          {isParent ? (
            <>
              {/* ---- handover ---- */}
              <Eyebrow style={{ marginBottom: 8 }}>交代 · Handover</Eyebrow>
              <Card padding="md" style={{ marginBottom: 18 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-3)',
                  }}
                >
                  <div style={{ fontSize: 'var(--text-sm)' }}>
                    交代の時間
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>Handover time</div>
                  </div>
                  <input
                    type="time"
                    value={handover}
                    onChange={(e) => setHandover(e.target.value)}
                    onBlur={() => familyId && void api.setHandoverAt(familyId, handover)}
                    aria-label="交代の時間"
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-md)',
                      color: 'var(--text)',
                      background: 'transparent',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 'var(--radius-xs)',
                      padding: '6px 8px',
                    }}
                  />
                </div>
              </Card>

              {/* ---- family members ---- */}
              <Eyebrow style={{ marginBottom: 8 }}>家族 · Family</Eyebrow>
              <Card padding="none" style={{ marginBottom: 10, overflow: 'hidden' }}>
                {parents.map((member, i) => (
                  <div
                    key={member.uid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '12px 15px',
                      borderBottom: i === parents.length - 1 ? 'none' : '1px solid var(--border)',
                    }}
                  >
                    <Avatar initial={member.initial} color={roleTone(member.role as ParentRole).solid} size={30} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{member.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{member.nameEn}</div>
                    </div>
                    {member.uid === me?.uid ? (
                      <span style={{ fontSize: 10, color: 'var(--text-subtle)' }}>あなた</span>
                    ) : null}
                  </div>
                ))}
              </Card>

              {/* Only show the code while there is someone left to invite. */}
              {parents.length < 2 && codes?.inviteCode ? (
                <Card padding="md" style={{ marginBottom: 18 }}>
                  <Eyebrow style={{ marginBottom: 6 }}>招待コード · Invite code</Eyebrow>
                  <div className="cjk" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 10 }}>
                    パートナーにこのコードを伝えると、同じ記録を使えます。
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Mono
                      style={{
                        flex: 1,
                        fontSize: 'var(--text-xl)',
                        letterSpacing: '0.12em',
                        background: 'var(--surface-sunken)',
                        borderRadius: 'var(--radius-md)',
                        padding: '10px 14px',
                        textAlign: 'center',
                      }}
                    >
                      {codes.inviteCode}
                    </Mono>
                    <IconButton
                      icon="Copy"
                      label="コードをコピー"
                      variant="soft"
                      onClick={() => void copy(codes.inviteCode, '招待コード')}
                    />
                  </div>
                </Card>
              ) : null}

              {/* ---- grandparents ---- */}
              <Eyebrow style={{ marginBottom: 8 }}>共有 · Grandparents</Eyebrow>
              <Card padding="md" style={{ marginBottom: 18 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-3)',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>アルバムを共有する</div>
                    <div className="cjk" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                      写真を見るだけ。記録は見えません。
                    </div>
                  </div>
                  <Switch
                    checked={family?.shareEnabled ?? false}
                    onChange={(next) => familyId && void api.setShareEnabled(familyId, next)}
                    aria-label="アルバムを共有する"
                  />
                </div>

                {family?.shareEnabled ? (
                  <>
                    <Button
                      variant="outline"
                      fullWidth
                      iconStart="Share2"
                      style={{ marginTop: 14 }}
                      onClick={() => void share()}
                    >
                      リンクを送る · Share link
                    </Button>
                    {viewers.length ? (
                      <div style={{ marginTop: 12 }}>
                        <Eyebrow style={{ marginBottom: 6 }}>見ている人 · Viewers</Eyebrow>
                        {viewers.map((v) => (
                          <div
                            key={v.uid}
                            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 0' }}
                          >
                            <Avatar initial={v.initial} color="var(--sand-500)" size={24} />
                            <span style={{ fontSize: 'var(--text-sm)' }}>{v.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </Card>

              {/* ---- children ---- */}
              <Eyebrow style={{ marginBottom: 8 }}>お子さん · Children</Eyebrow>
              <Card padding="none" style={{ marginBottom: 18, overflow: 'hidden' }}>
                {children.map((child, i) => (
                  <div
                    key={child.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '12px 15px',
                      borderBottom: i === children.length - 1 ? 'none' : '1px solid var(--border)',
                    }}
                  >
                    <span
                      style={{
                        flex: 'none',
                        width: 30,
                        height: 30,
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--brand-subtle)',
                        color: 'var(--brand)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name={child.kind === 'baby' ? 'Baby' : 'Backpack'} size={16} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{child.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                        {ageLabel(child.birthDate)}
                        {child.sex === 'unspecified' ? ' · 性別未設定' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            </>
          ) : null}

          <Button variant="ghost" fullWidth iconStart="LogOut" onClick={() => void signOut()}>
            サインアウト
          </Button>

          <div style={{ height: 20 }} />
        </Screen>
      </div>
    </div>
  );
}
